/**
 * MEVIA v4.0 - My Team Dashboard
 * Manager view for direct reports and their evaluations
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Users, UserCheck, TrendingUp, TrendingDown, AlertTriangle,
  BarChart3, FileText, Target, Calendar, ChevronRight,
  RefreshCw, Eye, Star, Activity, Gauge
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import logger from '../utils/logger';

const TEXTS = {
  es: {
    title: 'Mi Equipo',
    subtitle: 'Dashboard de reportes directos',
    
    // Summary
    directReports: 'Reportes Directos',
    indirectReports: 'Reportes Indirectos',
    averageIPS: 'Promedio SCORE MEVIA',
    atRiskEmployees: 'En Riesgo',
    activePlans: 'Planes Activos',
    
    // Tabs
    overview: 'Resumen',
    team: 'Equipo',
    evaluations: 'Evaluaciones',
    actionPlans: 'Planes de Acción',
    
    // Team
    allMembers: 'Todos',
    directOnly: 'Solo Directos',
    includeIndirect: 'Incluir Indirectos',
    riskOnly: 'Solo en Riesgo',
    
    // IPS Levels
    excellent: 'Excelente',
    good: 'Bueno',
    moderate: 'Moderado',
    atRisk: 'En Riesgo',
    unknown: 'Sin Evaluar',
    
    // Table headers
    employee: 'Empleado',
    position: 'Puesto',
    area: 'Área',
    scoreMevia: 'SCORE MEVIA',
    lastEval: 'Última Evaluación',
    plans: 'Planes',
    actions: 'Acciones',
    
    // Actions
    viewDetail: 'Ver Detalle',
    viewReport: 'Ver Reporte',
    refresh: 'Actualizar',
    
    // Messages
    noTeam: 'No tienes reportes directos asignados',
    noEvaluations: 'No hay evaluaciones registradas',
    noPlans: 'No hay planes de acción',
    loading: 'Cargando...',
    
    // Pulse
    teamPulse: 'Pulso del Equipo',
    teamIpm: 'IPM del Equipo',
    participation: 'Participación',
    responded: 'respondieron',
    of: 'de',
    teamDimensions: 'Dimensiones del Equipo',
    notCompleted: 'No han respondido',
    noTeamPulseData: 'Aún no hay datos de pulso para tu equipo.',
    minNotMet: 'Se requieren al menos 3 respuestas para el agregado.',
    privacyNote: 'Datos individuales requieren equipo de 5+ personas.',
    individualIpm: 'IPM Individual',
  },
  en: {
    title: 'My Team',
    subtitle: 'Direct reports dashboard',
    
    // Summary
    directReports: 'Direct Reports',
    indirectReports: 'Indirect Reports',
    averageIPS: 'Average MEVIA SCORE',
    atRiskEmployees: 'At Risk',
    activePlans: 'Active Plans',
    
    // Tabs
    overview: 'Overview',
    team: 'Team',
    evaluations: 'Evaluations',
    actionPlans: 'Action Plans',
    
    // Team
    allMembers: 'All',
    directOnly: 'Direct Only',
    includeIndirect: 'Include Indirect',
    riskOnly: 'At Risk Only',
    
    // IPS Levels
    excellent: 'Excellent',
    good: 'Good',
    moderate: 'Moderate',
    atRisk: 'At Risk',
    unknown: 'Not Evaluated',
    
    // Table headers
    employee: 'Employee',
    position: 'Position',
    area: 'Area',
    scoreMevia: 'MEVIA SCORE',
    lastEval: 'Last Evaluation',
    plans: 'Plans',
    actions: 'Actions',
    
    // Actions
    viewDetail: 'View Detail',
    viewReport: 'View Report',
    refresh: 'Refresh',
    
    // Messages
    noTeam: 'You have no direct reports assigned',
    noEvaluations: 'No evaluations recorded',
    noPlans: 'No action plans',
    loading: 'Loading...',
    
    // Pulse
    teamPulse: 'Team Pulse',
    teamIpm: 'Team IPM',
    participation: 'Participation',
    responded: 'responded',
    of: 'of',
    teamDimensions: 'Team Dimensions',
    notCompleted: 'Not Completed',
    noTeamPulseData: 'No pulse data for your team yet.',
    minNotMet: 'Minimum 3 responses required for aggregate.',
    privacyNote: 'Individual data requires team of 5+ members.',
    individualIpm: 'Individual IPM',
  }
};

const IPS_COLORS = {
  excellent: '#10B981',
  good: '#3B82F6',
  moderate: '#F59E0B',
  at_risk: '#EF4444',
  unknown: '#9CA3AF'
};

const MyTeamDashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;
  const managerId = user?.id;

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [actionPlans, setActionPlans] = useState([]);
  const [ipsDistribution, setIpsDistribution] = useState(null);
  
  // Filters
  const [includeIndirect, setIncludeIndirect] = useState(false);
  const [filterRisk, setFilterRisk] = useState(false);
  
  // Detail panel
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Team Pulse
  const [teamPulse, setTeamPulse] = useState(null);

  useEffect(() => {
    if (managerId) {
      loadAllData();
    }
  }, [managerId]);

  useEffect(() => {
    if (managerId) {
      loadMembers();
    }
  }, [includeIndirect, filterRisk]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSummary(),
        loadMembers(),
        loadEvaluations(),
        loadActionPlans(),
        loadIpsDistribution(),
        loadTeamPulse()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await axios.get(`/my-team/summary/${managerId}`);
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading summary:', error);
    }
  };

  const loadMembers = async () => {
    try {
      const response = await axios.get(`/my-team/members/${managerId}`, {
        params: {
          include_indirect: includeIndirect,
          filter_risk: filterRisk
        }
      });
      if (response.data.success) {
        setMembers(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading members:', error);
    }
  };

  const loadEvaluations = async () => {
    try {
      const response = await axios.get(`/my-team/evaluations/${managerId}`);
      if (response.data.success) {
        setEvaluations(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading evaluations:', error);
    }
  };

  const loadActionPlans = async () => {
    try {
      const response = await axios.get(`/my-team/action-plans/${managerId}`);
      if (response.data.success) {
        setActionPlans(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading action plans:', error);
    }
  };

  const loadIpsDistribution = async () => {
    try {
      const response = await axios.get(`/my-team/ips-distribution/${managerId}`);
      if (response.data.success) {
        setIpsDistribution(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading IPS distribution:', error);
    }
  };

  const API_URL = '';
  const loadTeamPulse = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v5/pulso/manager/team-pulse?lang=${language}`);
      if (res.data.success) setTeamPulse(res.data.data);
    } catch (e) { logger.error('Error loading team pulse:', e); }
  };

  const openMemberDetail = async (member) => {
    setSelectedMember(member);
    setDetailOpen(true);
    
    try {
      const response = await axios.get(`/my-team/member/${member.id}/detail`, {
        params: { manager_id: managerId }
      });
      if (response.data.success) {
        setMemberDetail(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading member detail:', error);
      toast.error(texts.error);
    }
  };

  const getIpsLevelLabel = (level) => {
    const labels = {
      excellent: texts.excellent,
      good: texts.good,
      moderate: texts.moderate,
      at_risk: texts.atRisk,
      unknown: texts.unknown
    };
    return labels[level] || level;
  };

  const getIpsLevelColor = (level) => {
    return IPS_COLORS[level] || IPS_COLORS.unknown;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US');
    } catch {
      return dateStr;
    }
  };

  const pieChartData = ipsDistribution ? [
    { name: texts.excellent, value: ipsDistribution.distribution.excellent?.count || 0, color: IPS_COLORS.excellent },
    { name: texts.good, value: ipsDistribution.distribution.good?.count || 0, color: IPS_COLORS.good },
    { name: texts.moderate, value: ipsDistribution.distribution.moderate?.count || 0, color: IPS_COLORS.moderate },
    { name: texts.atRisk, value: ipsDistribution.distribution.at_risk?.count || 0, color: IPS_COLORS.at_risk },
    { name: texts.unknown, value: ipsDistribution.distribution.unknown?.count || 0, color: IPS_COLORS.unknown },
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{texts.title}</h1>
          <p className="text-gray-500">{texts.subtitle}</p>
        </div>
        <Button variant="outline" onClick={loadAllData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {texts.refresh}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{texts.directReports}</p>
                <p className="text-2xl font-bold">{summary?.total_reportes_directos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserCheck className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{texts.indirectReports}</p>
                <p className="text-2xl font-bold">{summary?.total_reportes_indirectos || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{texts.averageIPS}</p>
                <p className="text-2xl font-bold">{summary?.promedio_ips?.toFixed(1) || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{texts.atRiskEmployees}</p>
                <p className="text-2xl font-bold text-red-600">{summary?.empleados_en_riesgo || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Target className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{texts.activePlans}</p>
                <p className="text-2xl font-bold">{summary?.planes_activos_total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            {texts.overview}
          </TabsTrigger>
          <TabsTrigger value="team" className="gap-2">
            <Users className="h-4 w-4" />
            {texts.team}
          </TabsTrigger>
          <TabsTrigger value="evaluations" className="gap-2">
            <FileText className="h-4 w-4" />
            {texts.evaluations}
          </TabsTrigger>
          <TabsTrigger value="plans" className="gap-2">
            <Target className="h-4 w-4" />
            {texts.actionPlans}
          </TabsTrigger>
          <TabsTrigger value="pulse" className="gap-2" data-testid="team-pulse-tab">
            <Activity className="h-4 w-4" />
            {texts.teamPulse}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* IPS Distribution Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {language === 'es' ? 'Distribución SCORE MEVIA' : 'MEVIA SCORE Distribution'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pieChartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-500">
                    {texts.noTeam}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* At Risk Employees */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  {language === 'es' ? 'Empleados en Riesgo' : 'At Risk Employees'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ipsDistribution?.distribution.at_risk?.members?.length > 0 ? (
                  <div className="space-y-3">
                    {ipsDistribution.distribution.at_risk.members.map((member) => (
                      <div 
                        key={member.id} 
                        className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100"
                        onClick={() => openMemberDetail(member)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-red-200 text-red-700 text-xs">
                              {member.nombre?.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{member.nombre}</span>
                        </div>
                        <Badge variant="destructive">
                          {member.ips_score?.toFixed(1) || '-'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Star className="h-12 w-12 mx-auto mb-3 text-green-300" />
                    <p>{language === 'es' ? 'No hay empleados en riesgo' : 'No at-risk employees'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <Button
              variant={!includeIndirect && !filterRisk ? "default" : "outline"}
              size="sm"
              onClick={() => { setIncludeIndirect(false); setFilterRisk(false); }}
            >
              {texts.directOnly}
            </Button>
            <Button
              variant={includeIndirect ? "default" : "outline"}
              size="sm"
              onClick={() => setIncludeIndirect(!includeIndirect)}
            >
              {texts.includeIndirect}
            </Button>
            <Button
              variant={filterRisk ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterRisk(!filterRisk)}
              className={filterRisk ? "bg-red-600 hover:bg-red-700" : ""}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {texts.riskOnly}
            </Button>
          </div>

          {/* Team Table */}
          <Card>
            <CardContent className="p-0">
              {members.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{texts.noTeam}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{texts.employee}</TableHead>
                      <TableHead>{texts.position}</TableHead>
                      <TableHead>{texts.area}</TableHead>
                      <TableHead>{texts.scoreMevia}</TableHead>
                      <TableHead>{texts.lastEval}</TableHead>
                      <TableHead>{texts.plans}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={member.foto_url} />
                              <AvatarFallback className="text-xs">
                                {member.nombre?.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{member.nombre}</p>
                              <p className="text-xs text-gray-500">{member.email}</p>
                            </div>
                            {!member.es_reporte_directo && (
                              <Badge variant="outline" className="text-xs">
                                {language === 'es' ? 'Indirecto' : 'Indirect'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{member.puesto_nombre || '-'}</TableCell>
                        <TableCell>{member.area_nombre || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: getIpsLevelColor(member.ips_nivel) }}
                            />
                            <span className="font-medium">
                              {member.ips_score?.toFixed(1) || '-'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {member.ultima_evaluacion_fecha ? (
                            <div className="text-sm">
                              <p>{formatDate(member.ultima_evaluacion_fecha)}</p>
                              <p className="text-xs text-gray-500">{member.ultima_evaluacion_tipo}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{member.planes_activos}</Badge>
                            <span className="text-xs text-gray-500">
                              / {member.planes_completados} {language === 'es' ? 'comp.' : 'done'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openMemberDetail(member)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {texts.viewDetail}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evaluations Tab */}
        <TabsContent value="evaluations">
          <Card>
            <CardContent className="p-0">
              {evaluations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{texts.noEvaluations}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{texts.employee}</TableHead>
                      <TableHead>{language === 'es' ? 'Tipo' : 'Type'}</TableHead>
                      <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                      <TableHead>{language === 'es' ? 'Fecha' : 'Date'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {evaluations.map((eval_) => (
                      <TableRow key={eval_.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{eval_.empleado_nombre}</p>
                            <p className="text-xs text-gray-500">{eval_.empleado_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{eval_.assessment_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={eval_.status === 'completed' ? 'success' : 'secondary'}
                          >
                            {eval_.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(eval_.completed_at || eval_.started_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Action Plans Tab */}
        <TabsContent value="plans">
          <Card>
            <CardContent className="p-0">
              {actionPlans.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{texts.noPlans}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{texts.employee}</TableHead>
                      <TableHead>{language === 'es' ? 'Plan' : 'Plan'}</TableHead>
                      <TableHead>{language === 'es' ? 'Prioridad' : 'Priority'}</TableHead>
                      <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                      <TableHead>{language === 'es' ? 'Vencimiento' : 'Due Date'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {actionPlans.map((plan) => (
                      <TableRow key={plan.id}>
                        <TableCell className="font-medium">{plan.empleado_nombre}</TableCell>
                        <TableCell>{plan.title}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={plan.priority === 'high' ? 'destructive' : 'outline'}
                          >
                            {plan.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={plan.status === 'completed' ? 'success' : 'secondary'}
                          >
                            {plan.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(plan.due_date)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Pulse Tab (M13 Fase 5) */}
        <TabsContent value="pulse" className="space-y-4" data-testid="team-pulse-content">
          {!teamPulse || !teamPulse.has_data ? (
            <div className="text-center py-12 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>{texts.noTeamPulseData}</p>
              {teamPulse?.reason === 'no_team' && <p className="text-xs text-gray-400 mt-1">{texts.noTeam}</p>}
            </div>
          ) : (
            <>
              {/* KPI Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-gray-500 mb-1">{texts.teamIpm}</p>
                    {teamPulse.min_responded_met ? (
                      <>
                        <p className="text-3xl font-bold" style={{ color: teamPulse.team_level === 'ALTO' ? '#22c55e' : teamPulse.team_level === 'MODERADO' ? '#eab308' : teamPulse.team_level === 'ELEVADO' ? '#f97316' : '#ef4444' }}>
                          {teamPulse.team_ipm}
                        </p>
                        <Badge className="mt-1 text-xs" style={{ backgroundColor: teamPulse.team_level === 'ALTO' ? '#22c55e' : teamPulse.team_level === 'MODERADO' ? '#eab308' : teamPulse.team_level === 'ELEVADO' ? '#f97316' : '#ef4444', color: '#fff' }}>
                          {teamPulse.team_level}
                        </Badge>
                      </>
                    ) : (
                      <p className="text-xs text-gray-400 mt-2">{texts.minNotMet}</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-gray-500 mb-1">{texts.participation}</p>
                    <p className="text-3xl font-bold text-teal-600">{teamPulse.participation_rate}%</p>
                    <p className="text-xs text-gray-400 mt-1">{teamPulse.completed_count} {texts.responded} {texts.of} {teamPulse.team_size}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-gray-500 mb-1">{texts.directReports}</p>
                    <p className="text-3xl font-bold">{teamPulse.team_size}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <p className="text-xs text-gray-500 mb-1">{language === 'es' ? 'Encuesta' : 'Survey'}</p>
                    <p className="text-sm font-medium text-gray-700 mt-1">{teamPulse.survey_name}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Team Dimensions */}
              {teamPulse.team_dimensions?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2 pt-4 px-5">
                    <CardTitle className="text-sm font-semibold text-gray-700">{texts.teamDimensions}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-4 space-y-2.5">
                    {teamPulse.team_dimensions.map(c => (
                      <div key={c.key} className="space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-700 font-medium">{c.label}</span>
                          <span style={{ color: c.level === 'ALTO' ? '#22c55e' : c.level === 'MODERADO' ? '#eab308' : c.level === 'ELEVADO' ? '#f97316' : '#ef4444' }} className="font-semibold">{c.iad}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="h-2 rounded-full transition-all duration-700"
                            style={{ width: `${Math.min(c.iad, 100)}%`, backgroundColor: c.level === 'ALTO' ? '#22c55e' : c.level === 'MODERADO' ? '#eab308' : c.level === 'ELEVADO' ? '#f97316' : '#ef4444' }} />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Individual IPMs (privacy >= 5) */}
              {teamPulse.show_individual && teamPulse.individual_list?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2 pt-4 px-5">
                    <CardTitle className="text-sm font-semibold text-gray-700">{texts.individualIpm}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{texts.employee}</TableHead>
                          <TableHead>{language === 'es' ? 'Depto' : 'Dept'}</TableHead>
                          <TableHead>IPM</TableHead>
                          <TableHead>{texts.level || 'Nivel'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {teamPulse.individual_list.map((emp, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{emp.name}</TableCell>
                            <TableCell className="text-gray-500 text-sm">{emp.department || '-'}</TableCell>
                            <TableCell className="font-bold">{emp.ipm?.toFixed(1)}</TableCell>
                            <TableCell>
                              <Badge className="text-xs" style={{ backgroundColor: emp.level === 'ALTO' ? '#22c55e' : emp.level === 'MODERADO' ? '#eab308' : emp.level === 'ELEVADO' ? '#f97316' : '#ef4444', color: '#fff' }}>
                                {emp.level}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
              {!teamPulse.show_individual && teamPulse.team_size < 5 && (
                <div className="text-center py-4 text-gray-400 text-xs flex items-center justify-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> {texts.privacyNote}
                </div>
              )}

              {/* Not Completed */}
              {teamPulse.not_completed?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2 pt-4 px-5">
                    <CardTitle className="text-sm font-semibold text-orange-600">{texts.notCompleted} ({teamPulse.not_completed.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-4">
                    <div className="divide-y">
                      {teamPulse.not_completed.map((nc, i) => (
                        <div key={i} className="flex justify-between items-center py-1.5 text-sm">
                          <span className="text-gray-700">{nc.name}</span>
                          <span className="text-gray-400 text-xs">{nc.email}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Member Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedMember?.nombre}</SheetTitle>
            <SheetDescription>{selectedMember?.email}</SheetDescription>
          </SheetHeader>
          
          {memberDetail ? (
            <div className="mt-6 space-y-6">
              {/* Basic Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{language === 'es' ? 'Información' : 'Information'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{texts.position}</span>
                    <span>{memberDetail.puesto_nombre || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{texts.area}</span>
                    <span>{memberDetail.area_nombre || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{language === 'es' ? 'Fecha Ingreso' : 'Hire Date'}</span>
                    <span>{formatDate(memberDetail.fecha_ingreso)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* IPS History */}
              {memberDetail.ips_history?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{language === 'es' ? 'Historial SCORE MEVIA' : 'MEVIA SCORE History'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {memberDetail.ips_history.slice(0, 5).map((ips, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-500">{formatDate(ips.calculated_at)}</span>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: getIpsLevelColor(get_ips_nivel(ips.ips_score)) }}
                            />
                            <span className="font-bold">{ips.ips_score?.toFixed(1)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Evaluations */}
              {memberDetail.evaluaciones?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{language === 'es' ? 'Evaluaciones Recientes' : 'Recent Evaluations'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {memberDetail.evaluaciones.map((eval_) => (
                        <div key={eval_.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <Badge variant="outline" className="text-xs">{eval_.assessment_type}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={eval_.status === 'completed' ? 'success' : 'secondary'}>
                              {eval_.status}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {formatDate(eval_.completed_at || eval_.started_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Plans */}
              {memberDetail.planes_accion?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{texts.actionPlans}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {memberDetail.planes_accion.map((plan) => (
                        <div key={plan.id} className="p-2 bg-gray-50 rounded">
                          <p className="font-medium text-sm">{plan.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={plan.status === 'completed' ? 'success' : 'secondary'} className="text-xs">
                              {plan.status}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {plan.due_date && `Due: ${formatDate(plan.due_date)}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600" />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

// Helper function for the component
const get_ips_nivel = (score) => {
  if (score === null || score === undefined) return "unknown";
  if (score >= 80) return "excellent";
  if (score >= 60) return "good";
  if (score >= 40) return "moderate";
  return "at_risk";
};

export default MyTeamDashboard;
